// wishlist.js - optimized replacement
document.addEventListener('DOMContentLoaded', () => {
  const WKEY = 'my-custom-rise-wishlist-key';
  const CACHE_KEY = 'wishlist_product_cache_v1';
  const CACHE_TTL = 1000 * 60 * 10; // 10 minutes

  const wishlist = {
    key: WKEY,
    products: [],
    cache: {},

    init() {
      this.loadFromStorage();
      this.loadCache();
      this.attachEvents();
      this.updateWishlistCount();
      this.updateAllButtons();

      // If wishlist page present, render immediately with placeholders
      if (document.getElementById('wishlist-container')) {
        this.renderWishlistPage();
      }

      // sync between tabs
      window.addEventListener('storage', (e) => {
        if (e.key === this.key) {
          this.loadFromStorage();
          this.updateAllButtons();
          this.updateWishlistCount();
          if (document.getElementById('wishlist-container')) {
            this.renderWishlistPage();
          }
        }
      });
    },

    loadFromStorage() {
      try {
        const stored = localStorage.getItem(this.key);
        this.products = stored ? JSON.parse(stored) : [];
      } catch (err) {
        this.products = [];
      }
    },

    saveToStorage() {
      localStorage.setItem(this.key, JSON.stringify(this.products));
      document.dispatchEvent(new Event('wishlist:updated'));
    },

    loadCache() {
      try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        const now = Date.now();
        // purge expired
        Object.keys(parsed).forEach(h => {
          if (!parsed[h] || (now - parsed[h].ts) > CACHE_TTL) {
            delete parsed[h];
          }
        });
        this.cache = parsed;
      } catch (e) {
        this.cache = {};
      }
    },

    saveCache() {
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(this.cache));
      } catch (e) {
        // ignore
      }
    },

    attachEvents() {
      // toggle wishlist from product lists/buttons
      document.body.addEventListener('click', (e) => {
        const btn = e.target.closest('.wishlist-button, .wishlist-toggle, [data-wishlist-toggle]');
        if (btn) {
          e.preventDefault();
          const handle = btn.dataset.productHandle || btn.dataset.handle || btn.getAttribute('data-handle');
          if (!handle) return;
          this.toggleProduct(handle);
          return;
        }

        const removeBtn = e.target.closest('.wishlist-remove');
        if (removeBtn) {
          e.preventDefault();
          const handle = removeBtn.dataset.handle;
          if (!handle) return;
          this.removeProduct(handle, /* animate */ true);
        }
      }, true);
    },

    toggleProduct(handle) {
      const idx = this.products.indexOf(handle);
      if (idx > -1) {
        this.products.splice(idx, 1);
      } else {
        this.products.push(handle);
      }
      this.saveToStorage();
      this.updateAllButtons();
      this.updateWishlistCount();
      if (document.getElementById('wishlist-container')) {
        this.renderWishlistPage(); // re-render (fast)
      }
    },

    removeProduct(handle, animate = false) {
      const idx = this.products.indexOf(handle);
      if (idx === -1) return;
      this.products.splice(idx, 1);
      this.saveToStorage();
      this.updateAllButtons();
      this.updateWishlistCount();

      // animate removal on wishlist page
      const card = document.querySelector(`.wishlist-card[data-handle="${handle}"]`);
      if (card && animate) {
        card.style.transition = 'opacity .25s ease, transform .25s ease';
        card.style.opacity = '0';
        card.style.transform = 'translateY(8px)';
        setTimeout(() => {
          if (card.parentNode) card.parentNode.removeChild(card);
          this.checkEmptyState();
        }, 260);
      } else {
        if (card && card.parentNode) card.parentNode.removeChild(card);
        this.checkEmptyState();
      }
    },

    updateAllButtons() {
      document.querySelectorAll('.wishlist-button, .wishlist-toggle, [data-wishlist-toggle]').forEach(button => {
        const handle = button.dataset.productHandle || button.dataset.handle || button.getAttribute('data-handle');
        if (!handle) return;
        if (this.products.includes(handle)) {
          button.classList.add('active');
          button.setAttribute('aria-pressed', 'true');
          button.setAttribute('title', 'Remove from wishlist');
        } else {
          button.classList.remove('active');
          button.setAttribute('aria-pressed', 'false');
          button.setAttribute('title', 'Add to wishlist');
        }
      });
    },

    updateWishlistCount() {
      const countEl = document.getElementById('wishlist-count');
      const wishlistIcon = document.querySelector('.header__icon--wishlist, [data-wishlist-icon]');
      const count = this.products.length;
      if (countEl) {
        if (count > 0) {
          countEl.textContent = String(count);
          countEl.classList.remove('hidden');
        } else {
          countEl.textContent = '';
          countEl.classList.add('hidden');
        }
      }
      if (wishlistIcon) {
        if (count > 0) wishlistIcon.classList.add('active');
        else wishlistIcon.classList.remove('active');
      }
    },

    async loadProduct(handle) {
      // return cached if available
      const cached = this.cache[handle];
      const now = Date.now();
      if (cached && (now - cached.ts) < CACHE_TTL && cached.data) {
        return cached.data;
      }

      try {
        const res = await fetch(`/products/${handle}.js`);
        if (!res.ok) throw new Error('product fetch failed');
        const json = await res.json();
        this.cache[handle] = { ts: Date.now(), data: json };
        this.saveCache();
        return json;
      } catch (err) {
        console.warn('Failed to fetch product', handle, err);
        return null;
      }
    },

    formatPrice(cents) {
      try {
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format((cents || 0) / 100);
      } catch (e) {
        return `Rs. ${(cents || 0) / 100}`;
      }
    },

    createPlaceholder(handle) {
      const el = document.createElement('div');
      el.className = 'wishlist-card wishlist-card--placeholder';
      el.setAttribute('data-handle', handle);
      el.innerHTML = `
        <div class="wishlist-card__skeleton">
          <div class="wishlist-card__img skeleton-box" aria-hidden="true"></div>
          <div class="wishlist-card__title skeleton-line" aria-hidden="true"></div>
          <div class="wishlist-card__price skeleton-line short" aria-hidden="true"></div>
        </div>
      `;
      return el;
    },

    createCardFromProduct(product, handle) {
      const wrapper = document.createElement('div');
      wrapper.className = 'wishlist-card';
      wrapper.setAttribute('data-handle', handle);

      const imageUrl = (product.images && product.images.length) ? product.images[0] : '';
      const productUrl = `/products/${handle}`;
      const variantId = (product.variants && product.variants[0]) ? product.variants[0].id : '';

      wrapper.innerHTML = `
        <a class="wishlist-card__link" href="${productUrl}">
          ${imageUrl ? `<img class="wishlist-card__img" src="${imageUrl}" alt="${this.escapeHtml(product.title)}" loading="lazy" decoding="async">` 
                     : `<div class="wishlist-card__img wishlist-card__noimg" aria-hidden="true"></div>`}
        </a>
        <div class="wishlist-card__content">
          <a class="wishlist-card__title" href="${productUrl}">${this.escapeHtml(product.title)}</a>
          <div class="wishlist-card__price">${this.formatPrice(product.price)}</div>
          <div class="wishlist-card__actions">
            <button class="wishlist-remove" data-handle="${handle}" aria-label="Remove from wishlist">Remove</button>
            ${variantId ? `<form class="wishlist-card__form" action="/cart/add" method="post" data-handle="${handle}">
              <input type="hidden" name="id" value="${variantId}">
              <button type="submit" class="wishlist-add-to-cart">Add to cart</button>
            </form>` : ''}
          </div>
        </div>
      `;
      // fade-in
      requestAnimationFrame(() => wrapper.classList.add('fade-in'));
      return wrapper;
    },

    escapeHtml(str) {
      return String(str).replace(/[&<>"']/g, (m) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[m]));
    },

    checkEmptyState() {
      const container = document.getElementById('wishlist-container');
      const emptyMessage = document.getElementById('wishlist-empty-message');
      if (!container || !emptyMessage) return;
      if (this.products.length === 0 || container.children.length === 0) {
        emptyMessage.style.display = 'block';
        container.style.display = 'none';
      } else {
        emptyMessage.style.display = 'none';
        container.style.display = 'grid';
      }
    },

    async renderWishlistPage() {
      const container = document.getElementById('wishlist-container');
      const emptyMessage = document.getElementById('wishlist-empty-message');
      if (!container || !emptyMessage) return;

      // clear and show skeleton placeholders instantly
      container.innerHTML = '';
      if (this.products.length === 0) {
        emptyMessage.style.display = 'block';
        container.style.display = 'none';
        return;
      }
      emptyMessage.style.display = 'none';
      container.style.display = 'grid';

      // create placeholders
      this.products.forEach(handle => {
        const ph = this.createPlaceholder(handle);
        container.appendChild(ph);
      });

      // load all products in parallel and populate placeholders as they resolve
      const loadPromises = this.products.map(async (handle) => {
        const product = await this.loadProduct(handle);
        const placeholder = container.querySelector(`.wishlist-card--placeholder[data-handle="${handle}"]`);
        if (!product) {
          // show unavailable card with remove button
          const u = document.createElement('div');
          u.className = 'wishlist-card wishlist-card--unavailable';
          u.setAttribute('data-handle', handle);
          u.innerHTML = `
            <div class="wishlist-card__noimg wishlist-card__img"></div>
            <div class="wishlist-card__content">
              <div class="wishlist-card__title">Product not found</div>
              <div class="wishlist-card__price">&nbsp;</div>
              <div class="wishlist-card__actions">
                <button class="wishlist-remove" data-handle="${handle}">Remove</button>
              </div>
            </div>
          `;
          if (placeholder) placeholder.replaceWith(u); else container.appendChild(u);
          return;
        }

        const card = this.createCardFromProduct(product, handle);
        if (placeholder) placeholder.replaceWith(card);
        else container.appendChild(card);
      });

      // do not await all to finish for perceived performance, but we will at least keep promise chain
      Promise.allSettled(loadPromises).then(() => {
        this.updateAllButtons();
        this.updateWishlistCount();
        this.checkEmptyState();
      }).catch(() => {
        this.updateAllButtons();
        this.updateWishlistCount();
        this.checkEmptyState();
      });
    }
  };

  // inject minimal styles for skeleton + cards (keeps it self-contained)
  (function injectStyles() {
    const style = document.createElement('style');
    style.id = 'wishlist-inline-styles';
    style.innerHTML = `
      #wishlist-container { display: grid; gap: 18px; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); align-items: start; }
      .wishlist-card {
        background: #fff;
        border-radius: 10px;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        padding: 16px;
        box-shadow: 0 2px 6px rgba(0,0,0,0.06);
      }

      .wishlist-card.fade-in { opacity:1; transform:translateY(0); }
      .wishlist-card__img {
        width: 100%;
        height: auto;
        aspect-ratio: 3 / 4; /* keeps consistent vertical layout */
        object-fit: contain; /* shows full image */
        border-radius: 10px;
        display: block;
        background-color: #fff;
        max-width: 240px; /* optional: limits huge images */
        margin: 0 auto;
      }

      .wishlist-card__noimg { width: 100px; height: 100px; background: linear-gradient(180deg,#f2f2f2,#e9e9e9); border-radius:6px; flex:0 0 100px; }
      .wishlist-card__content { flex:1 1 auto; display:flex; flex-direction:column; gap:6px; }
      .wishlist-card__title { font-weight:600; color:#111; text-decoration:none; }
      .wishlist-card__price { color:#444; font-size:0.95rem; }
      .wishlist-card__actions {
          margin-top: 12px;
          display: flex;
          justify-content: center; /* centers horizontally */
          align-items: center;
          gap: 10px;
        }

        .wishlist-remove, .wishlist-add-to-cart {
          min-width: 110px;
          text-align: center;
          justify-content: center;
        }
      .wishlist-remove, .wishlist-add-to-cart { background:#111; color:#fff; border:none; padding:8px 12px; border-radius:6px; cursor:pointer; font-size:0.9rem; }
      .wishlist-remove { background:#e60023; }
      /* skeleton */
      .wishlist-card--placeholder { background:transparent; box-shadow:none; padding:0; }
      .wishlist-card__skeleton { display:flex; gap:12px; align-items:flex-start; padding:10px; background:#fff; border-radius:8px; }
      .skeleton-box { width:100px; height:100px; border-radius:6px; background:linear-gradient(90deg,#eee 25%,#f5f5f5 50%,#eee 75%); background-size:200% 100%; animation:skeleton-shine 1.2s linear infinite; }
      .skeleton-line { height:12px; background:#eee; border-radius:6px; width:70%; }
      .skeleton-line.short { width:40%; }
      @keyframes skeleton-shine { from { background-position:200% 0 } to { background-position:-200% 0 } }
    `;
    document.head.appendChild(style);
  })();

  // start
  wishlist.init();
});
